/**
 * Dependency Matrix - Grid visualization for pattern detection
 * Issue #462: Alternative view for detecting cycles and patterns in large graphs
 */

import { useState, useMemo, useRef } from 'react';
import type { CallGraphData } from '@veodiagram/shared';

interface DependencyMatrixProps {
  data: CallGraphData;
  onSelectFunction?: (functionId: string) => void;
  onSelectEdge?: (sourceId: string, targetId: string) => void;
  maxNodes?: number;
}

type SortMode = 'alphabetical' | 'callers' | 'callees' | 'module';

export function DependencyMatrix({
  data,
  onSelectFunction,
  onSelectEdge,
  maxNodes = 100
}: DependencyMatrixProps) {
  const [sortMode, setSortMode] = useState<SortMode>('module');
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Build edge map for quick lookup
  const edgeMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const edge of data.edges) {
      const key = `${edge.source}|${edge.target}`;
      map.set(key, (map.get(key) || 0) + edge.callCount);
    }
    return map;
  }, [data.edges]);

  // Build caller/callee counts
  const { callerCounts, calleeCounts } = useMemo(() => {
    const callerCounts = new Map<string, number>();
    const calleeCounts = new Map<string, number>();

    for (const edge of data.edges) {
      calleeCounts.set(edge.source, (calleeCounts.get(edge.source) || 0) + 1);
      callerCounts.set(edge.target, (callerCounts.get(edge.target) || 0) + 1);
    }

    return { callerCounts, calleeCounts };
  }, [data.edges]);

  // Get unique modules
  const modules = useMemo(() => {
    const moduleSet = new Set<string>();
    for (const node of data.nodes) {
      moduleSet.add(node.module);
    }
    return Array.from(moduleSet).sort();
  }, [data.nodes]);

  // Filter and sort nodes for matrix
  const matrixNodes = useMemo(() => {
    let nodes = [...data.nodes];

    // Filter by module if selected
    if (selectedModule) {
      nodes = nodes.filter(n => n.module === selectedModule);
    }

    // Sort based on mode (with null-safe comparisons for module/name)
    nodes.sort((a, b) => {
      const aModule = a.module || '';
      const bModule = b.module || '';
      const aName = a.name || '';
      const bName = b.name || '';

      switch (sortMode) {
        case 'alphabetical':
          return aName.localeCompare(bName);
        case 'callers':
          return (callerCounts.get(b.id) || 0) - (callerCounts.get(a.id) || 0);
        case 'callees':
          return (calleeCounts.get(b.id) || 0) - (calleeCounts.get(a.id) || 0);
        case 'module':
          return aModule.localeCompare(bModule) || aName.localeCompare(bName);
        default:
          return 0;
      }
    });

    // Limit nodes for performance
    return nodes.slice(0, maxNodes);
  }, [data.nodes, selectedModule, sortMode, callerCounts, calleeCounts, maxNodes]);

  // Detect cycles
  const cycleNodes = useMemo(() => {
    const cycles = new Set<string>();
    const nodeIds = new Set(matrixNodes.map(n => n.id));

    // Simple cycle detection: A->B and B->A
    for (const nodeA of matrixNodes) {
      for (const nodeB of matrixNodes) {
        if (nodeA.id >= nodeB.id) continue;
        if (!nodeIds.has(nodeA.id) || !nodeIds.has(nodeB.id)) continue;

        const aToB = edgeMap.get(`${nodeA.id}|${nodeB.id}`);
        const bToA = edgeMap.get(`${nodeB.id}|${nodeA.id}`);

        if (aToB && bToA) {
          cycles.add(nodeA.id);
          cycles.add(nodeB.id);
        }
      }
    }

    return cycles;
  }, [matrixNodes, edgeMap]);

  // Get cell color based on call count
  const getCellColor = (callCount: number, isHovered: boolean, isCycle: boolean) => {
    if (callCount === 0) {
      return isHovered ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-900';
    }
    if (isCycle) {
      return 'bg-red-500';
    }
    if (callCount >= 10) return 'bg-blue-700';
    if (callCount >= 5) return 'bg-blue-500';
    if (callCount >= 3) return 'bg-blue-400';
    if (callCount >= 2) return 'bg-blue-300';
    return 'bg-blue-200';
  };

  // Cell size based on zoom
  const cellSize = Math.max(12, Math.floor(20 * zoom));
  const labelWidth = Math.max(100, Math.floor(150 * zoom));

  // Handle cell click
  const handleCellClick = (sourceId: string, targetId: string, callCount: number) => {
    if (callCount > 0 && onSelectEdge) {
      onSelectEdge(sourceId, targetId);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Controls */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 flex-wrap">
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
          className="px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        >
          <option value="module">Sort by Module</option>
          <option value="alphabetical">Sort Alphabetically</option>
          <option value="callers">Sort by Callers</option>
          <option value="callees">Sort by Callees</option>
        </select>

        <select
          value={selectedModule}
          onChange={(e) => setSelectedModule(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white max-w-xs"
        >
          <option value="">All Modules ({data.nodes.length} functions)</option>
          {modules.map(module => {
            const count = data.nodes.filter(n => n.module === module).length;
            return (
              <option key={module} value={module}>{module} ({count})</option>
            );
          })}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">Zoom:</label>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="w-24"
          />
        </div>

        <div className="flex-1" />

        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing {matrixNodes.length} of {data.nodes.length} functions
          {cycleNodes.size > 0 && (
            <span className="ml-2 text-red-600">
              ({cycleNodes.size} in cycles)
            </span>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 text-sm">
        <span className="text-gray-600 dark:text-gray-400">Legend:</span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-200 rounded" />
          <span>1 call</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-400 rounded" />
          <span>3+ calls</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-700 rounded" />
          <span>10+ calls</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-500 rounded" />
          <span>Cycle</span>
        </div>
      </div>

      {/* Matrix */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4">
        {matrixNodes.length > 0 ? (
          <div className="inline-block">
            {/* Column headers */}
            <div className="flex" style={{ marginLeft: labelWidth }}>
              {matrixNodes.map((node) => (
                <div
                  key={`col-${node.id}`}
                  className="relative cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  style={{ width: cellSize, height: labelWidth }}
                  title={node.qualifiedName}
                  onClick={() => onSelectFunction?.(node.id)}
                >
                  <span
                    className={`text-xs whitespace-nowrap ${
                      cycleNodes.has(node.id) ? 'text-red-600 font-bold' : 'text-gray-600 dark:text-gray-400'
                    }`}
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      left: '50%',
                      transform: 'rotate(-90deg)',
                      transformOrigin: 'left bottom',
                      width: labelWidth - 10,
                    }}
                  >
                    {node.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Rows */}
            {matrixNodes.map((rowNode, rowIdx) => (
              <div key={`row-${rowNode.id}`} className="flex">
                {/* Row label */}
                <div
                  className={`flex items-center justify-end pr-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    cycleNodes.has(rowNode.id) ? 'text-red-600 font-bold' : 'text-gray-600 dark:text-gray-400'
                  }`}
                  style={{ width: labelWidth, height: cellSize }}
                  title={rowNode.qualifiedName}
                  onClick={() => onSelectFunction?.(rowNode.id)}
                >
                  <span className="text-xs truncate">{rowNode.name}</span>
                </div>

                {/* Cells */}
                {matrixNodes.map((colNode, colIdx) => {
                  const callCount = edgeMap.get(`${rowNode.id}|${colNode.id}`) || 0;
                  const isHovered = hoveredCell?.row === rowIdx || hoveredCell?.col === colIdx;
                  const isCycle = cycleNodes.has(rowNode.id) && cycleNodes.has(colNode.id) &&
                                  edgeMap.has(`${rowNode.id}|${colNode.id}`) &&
                                  edgeMap.has(`${colNode.id}|${rowNode.id}`);
                  const isDiagonal = rowIdx === colIdx;

                  return (
                    <div
                      key={`cell-${rowNode.id}-${colNode.id}`}
                      className={`border border-gray-200 dark:border-gray-700 transition-colors cursor-pointer ${
                        isDiagonal ? 'bg-gray-300 dark:bg-gray-600' : getCellColor(callCount, isHovered, isCycle)
                      }`}
                      style={{ width: cellSize, height: cellSize }}
                      title={callCount > 0 ? `${rowNode.name} calls ${colNode.name} (${callCount}x)` : ''}
                      onMouseEnter={() => setHoveredCell({ row: rowIdx, col: colIdx })}
                      onMouseLeave={() => setHoveredCell(null)}
                      onClick={() => handleCellClick(rowNode.id, colNode.id, callCount)}
                    >
                      {cellSize >= 16 && callCount > 0 && (
                        <span className="flex items-center justify-center h-full text-xs text-white font-bold">
                          {callCount > 9 ? '9+' : callCount}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <p>No functions to display</p>
            <p className="text-sm mt-2">Select a module to view its dependency matrix</p>
          </div>
        )}
      </div>

      {/* Hover tooltip */}
      {hoveredCell && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-sm">
          {(() => {
            const rowNode = matrixNodes[hoveredCell.row];
            const colNode = matrixNodes[hoveredCell.col];
            const callCount = edgeMap.get(`${rowNode?.id}|${colNode?.id}`) || 0;
            if (!rowNode || !colNode) return null;
            return (
              <span className="text-gray-600 dark:text-gray-400">
                <strong>{rowNode.name}</strong> calls <strong>{colNode.name}</strong>
                {callCount > 0 ? ` (${callCount} time${callCount > 1 ? 's' : ''})` : ' (no direct calls)'}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default DependencyMatrix;
